import React, { useEffect, useState } from "react";
import {
  Datagrid,
  Filter,
  FunctionField,
  Loading,
  ResourceContextProvider,
  SearchInput,
  useQuery,
  TextField,
  ShowProps,
} from "react-admin";
import SanitizedGrid from "components/SanitizedGrid";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import CustomArrayChipField from "components/CustomArrayChipField";
import CustomDateField from "components/CustomDateField";
import CustomTimeField from "components/CustomTimeField";
import moment from "moment";
import { ETypeHoliday } from "pages/holidays/const";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import { PaddingDenseStyled2List } from "../../components/ListWrapper";
import CustomPagination from "../../components/CustomPagination";

const useStyles = makeStyles({
  message: {
    textAlign: "center",
    margin: "1em",
    color: "inherit",
  },
  icon: {
    width: "9em",
    height: "9em",
    opacity: 0.5,
  },
  toolbar: {
    textAlign: "center",
    marginTop: "2em",
  },
  noRecordLabel: {
    opacity: 0.5,
  },
  academicYearButton: {
    marginTop: "20px",
  },
  academicYearLabel: {
    marginTop: "5px",
  },
  MuiAccordionRoot: {
    "&.Mui-disabled": {
      backgroundColor: "transparent",
    },
  },
  MuiAccordionSummary: {
    minHeight: "64px",
    "& .MuiAccordionSummary-content": {
      margin: "10px 0px 20px",
    },
    "&.Mui-disabled": {
      opacity: 1,
    },
  },
});

const ThisFilter = (props: any) => (
  <Filter {...props}>
    <SearchInput source="search" alwaysOn />
  </Filter>
);

const Schedule = (props: ShowProps) => {
  const classes = useStyles();
  const [academicYearId, setAcademicYearId] = useState<number>();

  const { data, loading, error } = useQuery({
    type: "getList",
    resource: "academicYears",
    payload: {
      sort: { field: "id", order: "ASC" },
      filter: {
        SchoolId: parseInt(props.id),
        Date: moment().format("Y-MM-DD"),
      },
    },
  });

  // useEffect for set default AcademicYearId
  // i assume the default is a min index of AcademicYearId
  useEffect(() => {
    if (data) {
      setAcademicYearId(data[0]?.id);
    }
  }, [data]);

  if (loading) {
    return <Loading />;
  }
  if (error) {
    return <p>ERROR: {error}</p>;
  }

  return data.length > 0 ? (
    <Accordion defaultExpanded>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="regular-academic-year-content"
        id="regular-academic-year-header"
        className={classes.MuiAccordionSummary}
      >
        <Typography variant="h6">
          Current Regular Academic Year : {data[0].name}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <SanitizedGrid container spacing={2}>
          <Grid item xs={12}>
            <Typography variant="h6">Regular Academic Terms</Typography>
          </Grid>

          <Grid item xs={12}>
            <ResourceContextProvider value="academicTerms">
              <PaddingDenseStyled2List
                {...props}
                exporter={false}
                hasCreate={false}
                filters={<ThisFilter />}
                filterDefaultValues={{ AcademicYearId: academicYearId }}
                bulkActionButtons={false}
                title=" "
                pagination={<CustomPagination />}
              >
                <Datagrid>
                  <TextField source="name" label="Term" />
                  <CustomDateField source="startDate" label="From" />
                  <CustomDateField source="endDate" label="To" />
                </Datagrid>
              </PaddingDenseStyled2List>
            </ResourceContextProvider>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Regular Bus Schedules</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2">Going to School</Typography>
          </Grid>

          <Grid item xs={12}>
            <ResourceContextProvider value="busSchedules">
              <PaddingDenseStyled2List
                {...props}
                exporter={false}
                hasCreate={false}
                filters={<ThisFilter />}
                filterDefaultValues={{
                  SchoolId: parseInt(props.id),
                  Type: "ToSchool",
                }}
                bulkActionButtons={false}
                title=" "
                pagination={<CustomPagination />}
              >
                <Datagrid>
                  <TextField source="name" label="Wave Name" emptyText="-" />
                  <CustomTimeField source="arrivalTime" label="Bus Arrive by" />
                  <CustomTimeField
                    source="schoolTime"
                    label="School Start Time"
                  />
                  <FunctionField
                    label="Days of the Weeks"
                    render={(record: any) => {
                      let days: string = "";

                      if (record.isMonday) {
                        days = days + " Monday,";
                      }
                      if (record.isTuesday) {
                        days = days + " Tuesday,";
                      }
                      if (record.isWednesday) {
                        days = days + " Wednesday,";
                      }
                      if (record.isThursday) {
                        days = days + " Thursday,";
                      }
                      if (record.isFriday) {
                        days = days + " Friday,";
                      }

                      return days.replace(/,(?=[^,]*$)/, "");
                    }}
                  />
                  <CustomArrayChipField source="levels" chipSource="name" />
                </Datagrid>
              </PaddingDenseStyled2List>
            </ResourceContextProvider>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2">Going from School</Typography>
          </Grid>

          <Grid item xs={12}>
            <ResourceContextProvider value="busSchedules">
              <PaddingDenseStyled2List
                {...props}
                exporter={false}
                hasCreate={false}
                filters={<ThisFilter />}
                filterDefaultValues={{
                  SchoolId: parseInt(props.id),
                  Type: "FromSchool",
                }}
                bulkActionButtons={false}
                title=" "
                pagination={<CustomPagination />}
              >
                <Datagrid>
                  <TextField source="name" label="Wave Name" emptyText="-" />
                  <CustomTimeField source="arrivalTime" label="Bus Leave by" />
                  <CustomTimeField
                    source="schoolTime"
                    label="School End Time"
                  />
                  <FunctionField
                    label="Days of the Weeks"
                    render={(record: any) => {
                      let days: string = "";

                      if (record.isMonday) {
                        days = days + " Monday,";
                      }
                      if (record.isTuesday) {
                        days = days + " Tuesday,";
                      }
                      if (record.isWednesday) {
                        days = days + " Wednesday,";
                      }
                      if (record.isThursday) {
                        days = days + " Thursday,";
                      }
                      if (record.isFriday) {
                        days = days + " Friday,";
                      }

                      return days.replace(/,(?=[^,]*$)/, "");
                    }}
                  />
                  ;
                  <CustomArrayChipField source="levels" chipSource="name" />
                </Datagrid>
              </PaddingDenseStyled2List>
            </ResourceContextProvider>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6">Regular Holidays</Typography>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2">Regular School Holiday</Typography>
          </Grid>

          <Grid item xs={12}>
            <ResourceContextProvider value="holidays">
              <PaddingDenseStyled2List
                {...props}
                exporter={false}
                hasCreate={false}
                filters={<ThisFilter />}
                filterDefaultValues={{
                  SchoolId: Number(props.id),
                  AcademicYearId: academicYearId,
                  Type: ETypeHoliday.School,
                }}
                bulkActionButtons={false}
                title=" "
                pagination={<CustomPagination />}
              >
                <Datagrid>
                  <TextField source="name" label="Holiday" />
                  <CustomDateField source="date" />
                </Datagrid>
              </PaddingDenseStyled2List>
            </ResourceContextProvider>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2">Regular Public Holiday</Typography>
          </Grid>

          <Grid item xs={12}>
            <ResourceContextProvider value="holidays">
              <PaddingDenseStyled2List
                {...props}
                exporter={false}
                hasCreate={false}
                filters={<ThisFilter />}
                filterDefaultValues={{
                  SchoolId: Number(props.id),
                  AcademicYearId: academicYearId,
                  Type: ETypeHoliday.Public,
                }}
                bulkActionButtons={false}
                title=" "
                pagination={<CustomPagination />}
              >
                <Datagrid>
                  <TextField source="name" label="Holiday" />
                  <CustomDateField source="date" />
                </Datagrid>
              </PaddingDenseStyled2List>
            </ResourceContextProvider>
          </Grid>
        </SanitizedGrid>
      </AccordionDetails>
    </Accordion>
  ) : (
    <Accordion className={classes.MuiAccordionRoot} disabled>
      <AccordionSummary
        aria-controls="regular-academic-year-content"
        id="regular-academic-year-header"
        className={classes.MuiAccordionSummary}
      >
        <Typography variant="h6">
          No Regular Academic Year Active yet.
        </Typography>
      </AccordionSummary>
    </Accordion>
  );
};

export default Schedule;
